/**
 * 2026-09-24 (reworked 2026-09-26): shared types + a cached loader for forms
 * in Automation (Docs/RELEASE_NOTES_2026-09-24_FORMS.md). Forms are pre-chat
 * forms; GET /form-config returns them in the shape the pickers need.
 *
 * The catalog (checkpoints, answer types, kinds, collectors, repeat
 * policies, form condition fields, default settings) comes from the
 * backend's GET /form-config/meta -- the one source of truth
 * (backend/app/config/form_defaults.py) -- so the UI never keeps its own
 * copy of any of it.
 */
import { useEffect, useState } from 'react';
import { formConfigAPI } from '../../services/api';

export type LangMap = Record<string, string>;

export interface CatalogEntry {
  value: string;
  label: string;
  description?: string;
  can_hold?: boolean;
  available?: boolean;
  asked_as?: string;
  prefix?: boolean;
  events_only?: boolean;
  values?: string[];
  defaults?: Record<string, any>;
}

export interface FormMeta {
  checkpoints: CatalogEntry[];
  events: CatalogEntry[];
  answer_types: CatalogEntry[];
  form_kinds: CatalogEntry[];
  collectors: CatalogEntry[];
  repeat_policies: CatalogEntry[];
  enforcements: CatalogEntry[];
  condition_fields: CatalogEntry[];
  default_settings: Record<string, any>;
}

export interface FieldOption {
  value: string;
  label: LangMap | string;
}

/** One question of a form, as Automation sees it (from the pre-chat form's fields). */
export interface FormFieldSummary {
  key: string;
  label: string;
  answer_type: string;
  required?: boolean;
  options: FieldOption[];
}

/** A pre-chat form as Automation sees it (GET /form-config). The form
 * itself is edited in Settings -> Pre-chat Forms. */
export interface FormDef {
  id: number;
  key: string;
  name: string;
  kind: string;
  collector: string;
  enabled: boolean;
  version?: number;
  repeat_policy?: string | null;
  repeat_days?: number | null;
  archived_at?: string | null;
  fields: FormFieldSummary[];
  used_by?: Array<{ rule_id: number; name: string; enabled: boolean; trigger_event: string }>;
}

export interface FormConfig {
  forms: FormDef[];
  fields: FormFieldSummary[];
  settings: Record<string, any>;
  effective_settings: Record<string, any>;
}

let metaCache: FormMeta | null = null;
let metaPromise: Promise<FormMeta> | null = null;

export function loadFormMeta(): Promise<FormMeta> {
  if (metaCache) return Promise.resolve(metaCache);
  if (!metaPromise) {
    metaPromise = formConfigAPI
      .getMeta()
      .then((m: FormMeta) => {
        metaCache = m;
        return m;
      })
      .catch((err: any) => {
        metaPromise = null;
        throw err;
      });
  }
  return metaPromise;
}

export function useFormMeta(): FormMeta | null {
  const [meta, setMeta] = useState<FormMeta | null>(metaCache);
  useEffect(() => {
    let cancelled = false;
    loadFormMeta()
      .then((m) => {
        if (!cancelled) setMeta(m);
      })
      .catch(() => {
        if (!cancelled) setMeta(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);
  return meta;
}

/** Live forms + settings for pickers in the rule editor. Not cached
 * across mounts -- an admin may have just added a form in another tab. */
export function useFormConfig(): FormConfig | null {
  const [cfg, setCfg] = useState<FormConfig | null>(null);
  useEffect(() => {
    let cancelled = false;
    formConfigAPI
      .getConfig()
      .then((c: FormConfig) => {
        if (!cancelled) setCfg(c);
      })
      .catch(() => {
        if (!cancelled) setCfg(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);
  return cfg;
}

export const labelOf = (entries: CatalogEntry[] | undefined, value: string | null | undefined) =>
  (entries || []).find((e) => e.value === value)?.label || value || '';

/** Text for a language map, English first, else the first language present. */
export const textOf = (map: LangMap | string | null | undefined, lang = 'en') =>
  typeof map === 'string' ? map : (map && (map[lang] || map.en || Object.values(map).find(Boolean))) || '';

/** "Weekly check-in" -> "weekly_check_in" (the backend's key rule). */
export const toKey = (name: string) =>
  name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .replace(/^([^a-z])/, 'f_$1')
    .slice(0, 100);

export const apiError = (err: any, fallback: string) =>
  err?.response?.data?.detail
    ? typeof err.response.data.detail === 'string'
      ? err.response.data.detail
      : JSON.stringify(err.response.data.detail)
    : fallback;
